import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {BrevoApiKeyDto, ConfiguratorDataViewService, ConfiguratorDatastoreService, ConfiguratorStructureService, ConfiguratorTenantConfigurationService, DataViewAccessDTO, DatastoreDto, Partner, TablesAcessDTO, UserDTO, ConfiguratorFileConnectionService} from '@tj/tj-dmp-lib-api';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {AccountService} from '../../../../auth/account.service';

import {isAdminOfTenant} from '../../../../auth/admin-levels';
@Component({
    selector: 'dmp-datastore-details',
    templateUrl: './datastore-details.component.html',
    styleUrls: ['./datastore-details.component.scss'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class DatastoreDetailsComponent implements OnInit {
    breadcrumb = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.datastores')},
        {label: this.translate.instant('datastore.details')},
    ];

    datastore: DatastoreDto;
    partners: Partner[];

    tenantFormGroup: UntypedFormGroup;
    partnerFormGroup: UntypedFormGroup;

    pageReadOnly: boolean;
    showTable = true;
    newPartner: boolean = null;
    isSaving = false;
    public displayTableAccess = false;
    public tablesAccess: TablesAcessDTO | null = null;
    public displayDataViewAccess = false;
    public dataViewsAccess: DataViewAccessDTO | null = null;
    private idCurrentPartner: string | null = null;

    // Brevo API key status per partner (code -> configured)
    public brevoKeyStatus: Map<string, boolean> = new Map();
    public brevoStatusLoading = false;
    /** Nombre de connexions fichiers par affilié et par type (S3 / FTP). */
    public connectionCounts: Record<string, { S3: number; FTP: number }> = {};
    public connectionCountsLoading = false;
    public editPartnerDialog = false;
    public editPartnerForm: UntypedFormGroup;
    private editPartnerIndex = -1;

    // Brevo API key dialog
    public displayBrevoKey = false;
    public brevoPartnerCode: string | null = null;
    public brevoApiKeyConfigured = false;
    public brevoApiKeyInput = '';
    public brevoLoading = false;
    public brevoSaving = false;
    public brevoDeleting = false;

    // Connexions S3 / FTP : d'un affilié (code) ou du groupe (null)
    public displayConnections = false;
    public connectionsPartner: string | null = null;
    private account: UserDTO | null = null;

    // CORS origins dialog
    public displayCorsOrigins = false;
    public corsPartnerCode: string | null = null;
    public corsOriginsList: string[] = [];
    public corsLoading = false;
    public corsSaving = false;

    constructor(
        private datastoreService: ConfiguratorDatastoreService,
        private structureService: ConfiguratorStructureService,
        private dataViewService: ConfiguratorDataViewService,
        private tenantConfigService: ConfiguratorTenantConfigurationService,
        private fileConnectionService: ConfiguratorFileConnectionService,
        private messageService: MessageService,
        private accountService: AccountService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private readonly translate: TranslateService,
        private confirmationService: ConfirmationService
    ) { }

    ngOnInit(): void {
        this.accountService.identity().then(account => this.account = account);
        this.createTenantFormGroup();
        this.route.params.subscribe((params) => {
// Variable et astuce pour faire un reload du tableau au changement de route
            this.showTable = false;
            setTimeout(() => {
                this.showTable = true;
            });
            switch (params.action) {
                case 'view':
                    this.pageReadOnly = true;
                    this.tenantFormGroup.controls.tenantName.disable();
                    this.tenantFormGroup.controls.tenantDescription.disable();
                    this.tenantFormGroup.get('s3Settings')?.get('endpoint')?.disable();
                    this.tenantFormGroup.get('s3Settings')?.get('region')?.disable();
                    this.tenantFormGroup.get('s3Settings')?.get('bucketName')?.disable();
                    this.tenantFormGroup.get('s3Settings')?.get('accessKey')?.disable();
                    this.tenantFormGroup.get('s3Settings')?.get('secretKey')?.disable();
                    this.tenantFormGroup.get('s3Settings')?.disable();
                    break;
                case 'edit':
                    this.pageReadOnly = false;
                    this.tenantFormGroup.controls.tenantName.enable();
                    this.tenantFormGroup.controls.tenantDescription.enable();
                    this.tenantFormGroup.get('s3Settings')?.enable();
                    this.tenantFormGroup.get('s3Settings')?.get('endpoint')?.enable();
                    this.tenantFormGroup.get('s3Settings')?.get('region')?.enable();
                    this.tenantFormGroup.get('s3Settings')?.get('bucketName')?.enable();
                    this.tenantFormGroup.get('s3Settings')?.get('accessKey')?.enable();
                    this.tenantFormGroup.get('s3Settings')?.get('secretKey')?.enable();
                    this.tenantFormGroup.get('s3Enable')?.enable();
                    break;
                default:
                    this.router.navigate(['/h']);
                    break;
            }
        });
        this.loadDataFromId();
    }

    loadDataFromId() {
        this.datastoreService.getDatastoreUsingGET(this.route.snapshot.paramMap.get('id'))
            .subscribe(
                (res: DatastoreDto) => {
                    this.datastore = res;
                    this.partners = this.datastore.partners;
                    this.addDataToTenantFormGroup();
                    this.loadBrevoKeyStatuses();
                    this.loadConnectionCounts();
                },
                (res: HttpErrorResponse) => {
                    /** */
                }
            );
    }

    get partnersFormArray(): UntypedFormArray {
        return this.tenantFormGroup.controls.partners as UntypedFormArray;
    }

    get s3FormGroup(): UntypedFormGroup {
        return this.tenantFormGroup.get('s3Settings') as UntypedFormGroup;
    }

    createPartnerFormGroup(): UntypedFormGroup {
        return this.formBuilder.group({
            partnerCode: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(5)]],
            partnerName: [null, [Validators.required]],
            partnerDescription: null,
            partnerId: null,
            partnerDomaineSan: null,
            partnerSkipBrevo: [false],
            partnerContacts: this.formBuilder.array([])
        });
    }

    onSaveGroup(redirect = true): void {
        this.isSaving = true;
        this.datastore.name = this.tenantFormGroup.get('tenantName').value;
        this.datastore.description = this.tenantFormGroup.get('tenantDescription').value;
        const s3Enable = this.tenantFormGroup.get('s3Enable')?.value;
        this.datastore.s3Settings = s3Enable ? this.tenantFormGroup.get('s3Settings')?.getRawValue() : null;
        this.datastore.partners = this.partnersFormArray.value.map((e, index) => this.partnerFromRow(e, index));
        if (this.datastore.id) {
            this.subscribeToSaveResponse(this.datastoreService.updateDatastoreUsingPUT(this.datastore.code, this.datastore), redirect);
        } else {
            this.subscribeToSaveResponse(this.datastoreService.createDatastoreUsingPOST(this.datastore.code, this.datastore), redirect);
        }
    }

    /** Affilié tel qu'enregistré, complété des valeurs de sa ligne dans le formulaire. */
    private partnerFromRow(row: any, index: number): any {
        const formPartner = Object.entries(row)
            .filter(([key]) => key !== 'partnerContacts')
            .reduce((acc, [key, val]) => ({
                ...acc, [this.mapKeyPartnerReturn(key)]: val,
            }), {});
        return { ...this.partners[index], ...formPartner };
    }

    protected subscribeToSaveResponse(result: Observable<DatastoreDto>, redirect = true) {
        result.subscribe((saved: DatastoreDto) => {
            this.isSaving = false;
            if (redirect) {
                        this.router.navigate(['/h/settings/datastore', saved?.id ?? this.datastore.id, 'view']).then(() => this.loadDataFromId());
            } else {
                this.datastore = saved;
                this.partners = saved.partners;
            }
        }, () => {
            this.isSaving = false;
        });
    }

    mapKeyPartner(value: any) {
        return 'partner' + value[0].toUpperCase() + value.slice(1);
    }

    mapKeyPartnerReturn(value: any) {
        return value[7].toLowerCase() + value.slice(8);
    }

    addDataToTenantFormGroup(): void {
        this.tenantFormGroup.patchValue({
            tenantId: this.datastore.id,
            tenantName: this.datastore.name,
            tenantCode: this.datastore.code,
            tenantDescription: this.datastore.description,
            s3Enable: !!this.datastore?.s3Settings,
            s3Settings: {
                endpoint: this.datastore?.s3Settings?.endpoint ?? '',
                region: this.datastore?.s3Settings?.region ?? '',
                bucketName: this.datastore?.s3Settings?.bucketName ?? '',
                accessKey: this.datastore?.s3Settings?.accessKey ?? '',
                secretKey: this.datastore?.s3Settings?.secretKey ?? ''
            }
        });
        this.partnersFormArray.clear();
        this.partners.forEach(() => this.partnersFormArray.push(this.createPartnerFormGroup()));
        this.partnersFormArray.patchValue(this.partners.map((e) =>
            Object.entries(e).reduce((acc, curr) => ({
                    ...acc, [this.mapKeyPartner(curr[0])]: curr[1],
                }),
                {}
            )
        ));
    }

    createTenantFormGroup(): void {
        this.tenantFormGroup = this.formBuilder.group({
            tenantId: [{value: '', disabled: true}],
            tenantName: [{value: '', disabled: true}, [Validators.required]],
            tenantCode: [{value: '', disabled: true}],
            tenantDescription: [{value: '', disabled: true}],
            partners: this.formBuilder.array([]),
            s3Enable: [{value: false, disabled: true}],
            s3Settings: this.formBuilder.group({
                endpoint: [{
                    value: '',
                    disabled: true
                }, [
                    Validators.required,
                    // accept http(s)://host[:port][/], or bare host[:port]
                    Validators.pattern(/^((https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+(:\d+)?(\/)?)$/),
                ]
                ],
                region: [{
                    value: '',
                    disabled: true
                }, [
                    Validators.required,
                    // e.g. eu-west-1, us-east-1
                    Validators.pattern(/^[a-z]{2}-[a-z]+-\d$/),
                ]],
                bucketName: [{
                    value: '',
                    disabled: true
                }, [
                    Validators.required,
                    // 3-63 chars, lowercase letters, numbers, dots, hyphens, start/end alnum, no consecutive dots
                    Validators.pattern(/^(?!\.)((?!\.\.)[a-z0-9](?:[a-z0-9-\.]{1,61})[a-z0-9])$/),
                ]],
                accessKey: [
                    {
                        value: '',
                        disabled: true
                    },
                    [
                        Validators.required,
                        Validators.minLength(5),
                        Validators.maxLength(64)
                    ],
                ],
                secretKey: [
                    {
                        value: '',
                        disabled: true
                    },
                    [Validators.required, Validators.minLength(5), Validators.maxLength(128)],
                ]
            }),
        });
        // 🔄 Réagit à s3Enable
        this.tenantFormGroup.get('s3Enable')?.valueChanges.subscribe((enabled) => {
            this.syncS3EnableState(enabled === true);
        });
        this.syncS3EnableState(false);
    }

    private syncS3EnableState(enabled: boolean): void {
        const s3Group = this.tenantFormGroup.get('s3Settings') as UntypedFormGroup;
        if (!s3Group) {
            return;
        }

        if (enabled) {
            s3Group.enable({emitEvent: false});
        } else {
            s3Group.disable({emitEvent: false});
        }
        Object.values(s3Group.controls).forEach((c) => c.updateValueAndValidity({emitEvent: false}));
    }

    /** Annule l'édition : retour au détail, rechargé pour abandonner les modifications non enregistrées. */
    onCancelEdit(): void {
        this.router.navigate(['/h/settings/datastore', this.datastore.id, 'view']).then(() => this.loadDataFromId());
    }

    /** Crayon d'un affilié : édition dans une fenêtre, enregistrée seule (cf. onSavePartner). */
    onEditPartner(code: string): void {
        this.editPartnerIndex = this.partners.findIndex(p => p.code === code);
        if (this.editPartnerIndex < 0) {
            return;
        }
        this.editPartnerForm = this.createPartnerFormGroup();
        this.editPartnerForm.patchValue(this.partnersFormArray.at(this.editPartnerIndex).getRawValue());
        this.editPartnerDialog = true;
    }

    /**
     * Enregistre le seul affilié édité : le groupe tel qu'enregistré, avec cet affilié modifié. Les autres
     * modifications en cours de la page (nom, description…) restent à enregistrer.
     */
    onSavePartner(): void {
        const index = this.editPartnerIndex;
        if (index < 0 || !this.editPartnerForm || this.editPartnerForm.invalid) {
            return;
        }
        const values = this.editPartnerForm.getRawValue();
        const partner = this.partnerFromRow(values, index);
        const payload: DatastoreDto = {
            ...this.datastore,
            partners: this.datastore.partners.map((p, i) => i === index ? partner : p)
        };
        this.isSaving = true;
        this.datastoreService.updateDatastoreUsingPUT(this.datastore.code, payload).subscribe({
            next: (saved: DatastoreDto) => {
                this.isSaving = false;
                this.datastore = saved;
                this.partners = saved.partners;
                this.partnersFormArray.at(index).patchValue(values);
                this.editPartnerDialog = false;
            },
            error: () => this.isSaving = false
        });
    }

    addNewPartner(): void {
        this.newPartner = true;
        this.partnerFormGroup = this.createPartnerFormGroup();
    }

    onSaveNewPartner(): void {
        this.newPartner = false;
        this.partnersFormArray.push(this.partnerFormGroup);
        // Le groupe poussé et `partnerFormGroup` désignaient le même objet : `onCancelNewPartner()`,
        // désormais appelé aussi à la fermeture de la modale (croix comprise), aurait vidé le
        // partenaire qui vient d'être ajouté. On repart donc d'un groupe neuf.
        this.partnerFormGroup = this.createPartnerFormGroup();
    }

    /**
     * Erreur du code dans la modale de création de partenaire : elle prend la place du texte
     * d'aide sous le champ (cf. docs/ui-design-guidelines.md §11.2), `null` tant que la saisie
     * est valide ou n'a pas été touchée.
     */
    get newPartnerCodeError(): string | null {
        const code = this.partnerFormGroup?.get('partnerCode');
        if (!code?.invalid || !(code.dirty || code.touched)) {
            return null;
        }
        return code.errors?.required ? 'Le code est obligatoire.' : '2 caractères minimum.';
    }

    /**
     * Annulation de la création de partenaire, partagée entre le bouton « Annuler » et la croix.
     */
    onCancelNewPartner(): void {
        this.newPartner = false;
        this.partnerFormGroup.reset();
    }

    onEditTableAccess(idPartner: string) {
        this.idCurrentPartner = idPartner;
        this.structureService.getTableAccess(this.datastore.code, this.idCurrentPartner).subscribe(tablesAccess => {
            this.tablesAccess = tablesAccess;
            this.displayTableAccess = true;
        });
    }

    public saveAccessTable(): void {
        this.structureService.updateTableAccess(this.tablesAccess.tablesSelected, this.datastore.code, this.idCurrentPartner)
            .subscribe(res => {
                this.displayTableAccess = false;
            });
    }

    onEditDataViewAccess(idPartner: string) {
        this.idCurrentPartner = idPartner;
        this.dataViewService.getDataViewAccess(this.datastore.code, this.idCurrentPartner).subscribe(dataViewsAccess => {
            this.dataViewsAccess = dataViewsAccess;
            this.displayDataViewAccess = true;
        });
    }

    public saveAccessDataView(): void {
        this.dataViewService.updateDataViewAccess(this.dataViewsAccess.dataViewsSelected, this.datastore.code, this.idCurrentPartner)
            .subscribe(res => {
                this.displayDataViewAccess = false;
            });
    }

    public loadConnectionCounts(): void {
        if (!this.datastore?.code) {
            return;
        }
        this.connectionCountsLoading = true;
        this.fileConnectionService.getFileConnectionsUsingGET(this.datastore.code).subscribe({
            next: connections => {
                this.connectionCounts = (connections ?? []).filter(c => !!c.partner).reduce((acc, c) => {
                    const counts = acc[c.partner] ?? {S3: 0, FTP: 0};
                    counts[c.type === 'S3' ? 'S3' : 'FTP']++;
                    return {...acc, [c.partner]: counts};
                }, {} as Record<string, { S3: number; FTP: number }>);
                this.connectionCountsLoading = false;
            },
            error: () => {
                this.connectionCounts = {};
                this.connectionCountsLoading = false;
            }
        });
    }

    public loadBrevoKeyStatuses(): void {
        if (!this.partners || this.partners.length === 0) return;
        this.brevoStatusLoading = true;
        let remaining = this.partners.length;
        this.partners.forEach(partner => {
            this.tenantConfigService.getPartnerBrevoKeyUsingGET(this.datastore.code, partner.code)
                .subscribe({
                    next: (dto: BrevoApiKeyDto) => {
                        this.brevoKeyStatus.set(partner.code, dto.apiKeyConfigured ?? false);
                        remaining--;
                        if (remaining === 0) this.brevoStatusLoading = false;
                    },
                    error: () => {
                        this.brevoKeyStatus.set(partner.code, false);
                        remaining--;
                        if (remaining === 0) this.brevoStatusLoading = false;
                    }
                });
        });
    }

    /** Les connexions du groupe sont gérées par ses admins ; celles d'un affilié, par l'affilié aussi. */
    get canManageGroupConnections(): boolean {
        return isAdminOfTenant(this.account, this.datastore?.code);
    }

    onEditConnections(partnerCode: string | null): void {
        this.connectionsPartner = partnerCode;
        this.displayConnections = true;
    }

    onEditBrevoKey(partnerCode: string): void {
        this.brevoPartnerCode = partnerCode;
        this.brevoApiKeyInput = '';
        this.brevoApiKeyConfigured = false;
        this.brevoLoading = true;
        this.displayBrevoKey = true;

        this.tenantConfigService.getPartnerBrevoKeyUsingGET(this.datastore.code, partnerCode)
            .subscribe({
                next: (dto: BrevoApiKeyDto) => {
                    this.brevoApiKeyConfigured = dto.apiKeyConfigured ?? false;
                    this.brevoLoading = false;
                },
                error: () => {
                    this.brevoApiKeyConfigured = false;
                    this.brevoLoading = false;
                }
            });
    }

    saveBrevoKey(): void {
        if (this.pageReadOnly || this.brevoLoading || this.brevoSaving || this.brevoDeleting) {
            return;
        }
        if (!this.brevoApiKeyInput || !this.brevoApiKeyInput.trim()) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('datastore.brevoKeyRequiredWarn')
            });
            return;
        }
        this.brevoSaving = true;
        const dto: BrevoApiKeyDto = { apiKey: this.brevoApiKeyInput.trim() };

        this.tenantConfigService.savePartnerBrevoKeyUsingPUT(this.datastore.code, this.brevoPartnerCode, dto)
            .subscribe({
                next: (result: BrevoApiKeyDto) => {
                    this.brevoApiKeyConfigured = result.apiKeyConfigured ?? true;
                    this.brevoKeyStatus.set(this.brevoPartnerCode, this.brevoApiKeyConfigured);
                    this.brevoApiKeyInput = '';
                    this.brevoSaving = false;
                    this.displayBrevoKey = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('datastore.brevoKeySaved', {code: this.brevoPartnerCode})
                    });
                },
                error: () => {
                    this.brevoSaving = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('datastore.brevoKeySaveFailed')
                    });
                }
            });
    }

    deleteBrevoKey(): void {
        if (!this.canDeleteBrevoKey()) {
            return;
        }
        const datastoreCode = this.datastore.code;
        const partnerCode = this.brevoPartnerCode;
        this.confirmationService.confirm({
            key: 'delete-brevo-key',
            header: this.translate.instant('datastore.brevoKeyDelete'),
            message: this.translate.instant('datastore.brevoKeyDeleteConfirm', {code: partnerCode}),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('datastore.brevoKeyDelete'),
            acceptIcon: 'fas fa-trash',
            rejectLabel: this.translate.instant('common.cancel'),
            defaultFocus: 'reject',
            accept: () => {
                if (this.displayBrevoKey && this.datastore?.code === datastoreCode && this.brevoPartnerCode === partnerCode) {
                    this.performDeleteBrevoKey();
                }
            }
        });
    }

    private canDeleteBrevoKey(): boolean {
        return !this.pageReadOnly && this.brevoApiKeyConfigured && !!this.brevoPartnerCode
            && !this.brevoLoading && !this.brevoSaving && !this.brevoDeleting;
    }

    private performDeleteBrevoKey(): void {
        if (!this.canDeleteBrevoKey()) {
            return;
        }
        const partnerCode = this.brevoPartnerCode;
        this.brevoDeleting = true;
        this.tenantConfigService.deletePartnerBrevoKeyUsingDELETE(this.datastore.code, partnerCode)
            .subscribe({
                next: () => {
                    this.brevoApiKeyConfigured = false;
                    this.brevoKeyStatus.set(partnerCode, false);
                    this.brevoApiKeyInput = '';
                    this.brevoDeleting = false;
                    this.displayBrevoKey = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('datastore.brevoKeyDeleted', {code: partnerCode})
                    });
                },
                error: () => {
                    this.brevoDeleting = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('datastore.brevoKeyDeleteFailed')
                    });
                }
            });
    }

    public updateStatusS3(): void {

    }

    onEditCorsOrigins(partnerCode: string): void {
        this.corsPartnerCode = partnerCode;
        this.corsOriginsList = [];
        this.corsLoading = true;
        this.displayCorsOrigins = true;

        this.tenantConfigService.getPartnerCorsOriginsUsingGET(this.datastore.code, partnerCode)
            .subscribe({
                next: (origins: string[]) => {
                    this.corsOriginsList = origins ?? [];
                    this.corsLoading = false;
                },
                error: () => {
                    this.corsOriginsList = [];
                    this.corsLoading = false;
                }
            });
    }

    saveCorsOrigins(): void {
        this.corsSaving = true;
        this.tenantConfigService.savePartnerCorsOriginsUsingPUT(this.datastore.code, this.corsPartnerCode, this.corsOriginsList)
            .subscribe({
                next: (origins: string[]) => {
                    this.corsOriginsList = origins ?? [];
                    this.corsSaving = false;
                    this.displayCorsOrigins = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('datastore.corsSaved', {code: this.corsPartnerCode})
                    });
                },
                error: () => {
                    this.corsSaving = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('datastore.corsSaveFailed')
                    });
                }
            });
    }

    protected onOpenFicheClient(partner: string) {
        this.router.navigate([`/h/invoicing/company/edit/${this.datastore.code}/${partner}`]);
    }
}

